import { Grid, Typography } from "@mui/material";
import { blue, green, grey, orange, red, yellow } from '@mui/material/colors';
import * as api from "api/Core";
import { GlobalClassKeys } from "assets/jss/common-styles";
import { SpinnerIndicator } from "components/CallServer";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import DashDoughnut, { DoughnutCenterText } from "components/Dashboard/DashDoughnut";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import { KnowledgeTestResultAnalysisModel, TopicShortViewModel } from "models/ApiModels";
import * as React from "react";
import { Doughnut } from "react-chartjs-2";
import { WithTranslation } from "react-i18next";
/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    topic: TopicShortViewModel;
}

interface IState {
    data: KnowledgeTestResultAnalysisModel;
    lastError: any;
    loading: boolean;
}

type Props = IProps & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

export class TopicDashboardTrustAndRetention extends React.Component<Props, IState>{

    constructor(props: Props) {
        super(props);

        this.state = {
            data: null,
            lastError: null,
            loading: true,
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.Dashboards.Topic.TrustAndRetention." + r, f);
    }

    ///
    private loadData() {
        api.getTopicDashboardTrustAnalysis(this.props.topic.topicId)
            .then((result) => {
                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, lastError: error, loading: false });
            });
    }

    ///
    private chartTrust() {
        const { data } = this.state;
        const trustLowPercent = data.totalAnswers == 0 ? 0 : 100 * data.trustLow / data.totalAnswers;
        //const trustMediumPercent = data.totalAnswers == 0 ? 0 : 100 * data.trustMedium / data.totalAnswers;
        const trustHighPercent = data.totalAnswers == 0 ? 0 : 100 * data.trustHigh / data.totalAnswers;

        const centerLabel = data.totalAnswers == 0
            ? "-"
            : trustLowPercent > 30 && trustHighPercent < 15
                ? this.res("TrustLevel1")
                : trustLowPercent > 30 && trustLowPercent < 15
                    ? this.res("TrustLevel3")
                    : this.res("TrustLevel2");

        const chartData = {
            labels: [
                this.res("ChartLabel3"),
                this.res("ChartLabel2"),
                this.res("ChartLabel1"),
            ],
            datasets: [{
                data: [data.trustHigh, data.trustMedium, data.trustLow, data.totalAnswers == 0 ? 100 : 0],
                backgroundColor: [green[500], yellow[500], red[500], grey[500]],
            }],
            options: {
                elements: {
                    center: {
                        text: centerLabel,
                        color: grey.A200,
                        fontStyle: 'Arial',
                        sidePadding: 20
                    }
                }
            }
        };

        return <Doughnut
            data={chartData}
            options={
                {
                    responsive: false,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: true,
                            position: "right"
                        },
                        tooltip: {
                            enabled: false
                        }
                    }
                }
            }
            plugins={
                [
                    DoughnutCenterText
                ]
            }
        />;
    }

    ///
    private plotSimpleDoughnut(v: number, color: string) {

        const chartData = {
            datasets: [{
                data: [v, 100 - v],
                backgroundColor: [
                    color,
                    grey[500]
                ],
                options: {
                    elements: {
                        center: {
                            text: v.toFixed(0) + "%",
                            color: grey.A200,
                            fontStyle: 'Arial',
                            sidePadding: 20
                        }
                    }
                }
            }]
        };

        return <Doughnut
            data={chartData}
            options={
                {
                    plugins: {
                        legend: {
                            display: false
                        },
                        tooltip: {
                            enabled: false
                        }
                    }
                }
            }
            plugins={
                [
                    DoughnutCenterText
                ]
            }
        />;
    }

    ///
    private renderContent() {
        const { data } = this.state;

        const trustHighWrongPercent = data.totalAnswers == 0 ? 0 : 100 * data.trustHighWrong / data.totalAnswers;
        const trustHighCorrectPercent = data.totalAnswers == 0 ? 0 : 100 * data.trustHighCorrect / data.totalAnswers;
        const trustLowCorrectPercent = data.totalAnswers == 0 ? 0 : 100 * data.trustLowCorrect / data.totalAnswers;
        const retentionPercent = data.retentionAnswers == null ? null : 100 * data.retentionCorrect / data.retentionAnswers;

        return (
            <>
                <MuiContainer>
                    <Grid container justifyContent="space-around" alignItems="center" >
                        <Grid item>
                            <div className={GlobalClassKeys.textCenter}>
                                <MuiColor variant="black">
                                    <Typography variant="button"><b>{this.res("TrustChartTitle")}</b></Typography>
                                </MuiColor>
                            </div>
                            {this.chartTrust()}
                        </Grid>
                        <Grid item>
                            <div className={GlobalClassKeys.textCenter}>
                                <MuiColor variant="black" >
                                    <Typography variant="button"><b>{this.res("RetentionChartTitle")}</b></Typography>
                                </MuiColor>
                            </div>
                            <DashDoughnut value={retentionPercent} color={blue[500]} nullText="N/D" />
                        </Grid>
                    </Grid>
                </MuiContainer>

                <MuiContainer>
                    <Grid container justifyContent="space-around" alignItems="center" >
                        <Grid item>
                            <div className={GlobalClassKeys.textCenter}>
                                <MuiColor variant="error">
                                    <Typography variant="button"><b>{this.res("DoughnutWrongLabel1")}</b></Typography>
                                    <Typography variant="body2"><b>{this.res("DoughnutWrongLabel2")}</b></Typography>
                                </MuiColor>
                            </div>
                            <DashDoughnut value={trustHighWrongPercent} color={red[500]} />
                        </Grid>
                        <Grid item>
                            <div className={GlobalClassKeys.textCenter}>
                                <MuiColor variant="warning">
                                    <Typography variant="button"><b>{this.res("DoughnutLowCorrectLabel1")}</b></Typography>
                                    <Typography variant="body2"><b>{this.res("DoughnutLowCorrectLabel2")}</b></Typography>
                                </MuiColor>
                            </div>
                            <DashDoughnut value={trustLowCorrectPercent} color={orange[500]} />
                        </Grid>
                        <Grid item>
                            <div className={GlobalClassKeys.textCenter}>
                                <MuiColor variant="success">
                                    <Typography variant="button"><b>{this.res("DoughnutHighCorrectLabel1")}</b></Typography>
                                    <Typography variant="body2"><b>{this.res("DoughnutHighCorrectLabel2")}</b></Typography>
                                </MuiColor>
                            </div>
                            <DashDoughnut value={trustHighCorrectPercent} color={green[500]} />
                        </Grid>
                    </Grid>
                </MuiContainer>
            </>
        );
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public render() {
        const { loading, lastError } = this.state;

        return (
            <MuiContainer>
                {
                    loading
                        ? <SpinnerIndicator />
                        : lastError
                            ? <RequestErrorReport error={lastError} />
                            : this.renderContent()
                }
            </MuiContainer>
        );
    }
}